import { listAllExercisesOptions } from "@/actions/exercise/handler";
import {
  clearProgramAccessCodeFn,
  createProgramDayFn,
  deleteProgramDayFn,
  duplicateProgramFn,
  duplicateProgramWeekFn,
  generateProgramAccessCodeFn,
  getProgramOptions,
  getProgramParticipantCountOptions,
  listAllProgramsOptions,
  toggleProgramPublicFn,
  updateProgramDayFn,
  updateProgramFn,
} from "@/actions/program/handler";
import type {
  ProgramDayExercise,
  SelectProgramDay,
} from "@/api/db/schema/program-days";
import { DayEditor } from "@/components/admin/DayExerciseEditor";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { useMutation, useQuery, useSuspenseQuery } from "@tanstack/react-query";
import {
  Link,
  createFileRoute,
  redirect,
  useNavigate,
  useRouteContext,
} from "@tanstack/react-router";
import {
  ArrowLeft,
  Calendar,
  ChevronLeft,
  ChevronRight,
  Clock,
  Copy,
  ExternalLink,
  Globe,
  Lock,
  Plus,
  RefreshCw,
  Share2,
  Trash2,
  TrendingUp,
  Users,
  Video,
} from "lucide-react";
import { Suspense, useState } from "react";
import { toast } from "sonner";

export const Route = createFileRoute("/_app/admin/program/$programId")({
  beforeLoad: async ({ context }) => {
    if (!context.user || context.user.role !== "admin") {
      throw redirect({ to: "/" });
    }
  },
  loader: async ({ context, params }) =>
    await context.queryClient.ensureQueryData(
      getProgramOptions(params.programId),
    ),
  component: ProgramEditorPage,
});

function ProgramEditorPage() {
  const { programId } = Route.useParams();

  return (
    <div className="container mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
      {/* Breadcrumb */}
      <Button asChild variant="ghost" className="mb-6">
        <Link to="/admin/programs">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Programlar
        </Link>
      </Button>

      <Suspense fallback={<EditorSkeleton />}>
        <ProgramEditorContent programId={programId} />
      </Suspense>
    </div>
  );
}

function ProgramEditorContent({ programId }: { programId: string }) {
  const { queryClient } = useRouteContext({
    from: "/_app/admin/program/$programId",
  });
  const { data: programData } = useSuspenseQuery(getProgramOptions(programId));
  const { data: exercisesData } = useQuery(listAllExercisesOptions());

  const program = programData?.body;
  const isValidProgram = program && "days" in program;

  const navigate = useNavigate();
  const [selectedWeek, setSelectedWeek] = useState(1);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [editingDay, setEditingDay] = useState<SelectProgramDay | null>(null);
  const [deleteDay, setDeleteDay] = useState<SelectProgramDay | null>(null);
  const [isCopyWeekOpen, setIsCopyWeekOpen] = useState(false);
  const [targetWeek, setTargetWeek] = useState<number>(2);
  const [isCopyProgramOpen, setIsCopyProgramOpen] = useState(false);
  const [copyProgramName, setCopyProgramName] = useState("");

  // Form state for day editor
  const [dayName, setDayName] = useState("");
  const [exercises, setExercises] = useState<ProgramDayExercise[]>([]);
  const [notes, setNotes] = useState<string | null>(null);

  // Mutations - must be before any early return
  const duplicateProgramMutation = useMutation({
    mutationFn: duplicateProgramFn,
    onSuccess: (res) => {
      queryClient.invalidateQueries({
        queryKey: listAllProgramsOptions().queryKey,
      });
      toast.success(res.body?.message || "Program başarıyla kopyalandı");
      setIsCopyProgramOpen(false);
      if (res.body?.program?.programId) {
        navigate({
          to: "/admin/program/$programId",
          params: { programId: res.body.program.programId },
        });
      }
    },
    onError: (err) => {
      toast.error("Program kopyalanamadı", { description: err.message });
    },
  });

  const createDayMutation = useMutation({
    mutationFn: createProgramDayFn,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getProgramOptions(programId).queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: listAllProgramsOptions().queryKey,
      });
      toast.success("Gün oluşturuldu");
      closeEditor();
    },
    onError: (error) => {
      toast.error("Gün oluşturulamadı", { description: error.message });
    },
  });

  const updateDayMutation = useMutation({
    mutationFn: updateProgramDayFn,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getProgramOptions(programId).queryKey,
      });
      toast.success("Gün güncellendi");
      closeEditor();
    },
    onError: (error) => {
      toast.error("Gün güncellenemedi", { description: error.message });
    },
  });

  const deleteDayMutation = useMutation({
    mutationFn: deleteProgramDayFn,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getProgramOptions(programId).queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: listAllProgramsOptions().queryKey,
      });
      toast.success("Gün silindi");
      setDeleteDay(null);
    },
    onError: (error) => {
      toast.error("Gün silinemedi", { description: error.message });
    },
  });

  const duplicateWeekMutation = useMutation({
    mutationFn: duplicateProgramWeekFn,
    onSuccess: (response) => {
      queryClient.invalidateQueries({
        queryKey: getProgramOptions(programId).queryKey,
      });
      toast.success(response.body.message);
      setIsCopyWeekOpen(false);
      setSelectedWeek(targetWeek);
    },
    onError: (error) => {
      toast.error("Hafta kopyalanamadı", { description: error.message });
    },
  });

  const closeEditor = () => {
    setIsEditorOpen(false);
    setEditingDay(null);
    setDayName("");
    setExercises([]);
    setNotes(null);
  };

  if (!isValidProgram) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <p className="text-muted-foreground">Program bulunamadı</p>
        </CardContent>
      </Card>
    );
  }

  const days = program.days ?? [];
  const weekDays = days.filter(
    (d: { weekNumber: number }) => d.weekNumber === selectedWeek,
  );
  const totalWeeks = program.durationWeeks;

  // Available exercises for dropdown
  const availableExercises =
    exercisesData?.body?.exercises?.map(
      (e: { exerciseId: string; name: string }) => ({
        exerciseId: e.exerciseId,
        name: e.name,
      }),
    ) ?? [];

  const openEditor = (day?: SelectProgramDay) => {
    if (day) {
      setEditingDay(day);
      setDayName(day.dayName);
      setExercises(day.exercises || []);
      setNotes(day.notes);
    } else {
      setEditingDay(null);
      setDayName("");
      setExercises([]);
      setNotes(null);
    }
    setIsEditorOpen(true);
  };

  const handleSave = () => {
    if (!dayName.trim()) {
      toast.error("Gün adı gerekli");
      return;
    }

    if (editingDay) {
      updateDayMutation.mutate({
        data: {
          dayId: editingDay.dayId,
          dayName,
          exercises,
          notes,
        },
      });
    } else {
      const nextDayNumber =
        weekDays.length > 0
          ? Math.max(
              ...weekDays.map((d: { dayNumber: number }) => d.dayNumber),
            ) + 1
          : 1;
      createDayMutation.mutate({
        data: {
          programId,
          weekNumber: selectedWeek,
          dayNumber: nextDayNumber,
          dayName,
          exercises,
          notes: notes || undefined,
        },
      });
    }
  };

  const handleDeleteDay = () => {
    if (deleteDay) {
      deleteDayMutation.mutate({ data: { dayId: deleteDay.dayId } });
    }
  };

  const difficultyLabels = ["Başlangıç", "Kolay", "Orta", "Zor", "İleri"];

  return (
    <div className="space-y-6">
      {/* Program Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-bold text-2xl">{program.name}</h1>
          <div className="mt-1 flex items-center gap-4 text-muted-foreground text-sm">
            <span className="flex items-center gap-1">
              <Calendar className="h-4 w-4" />
              {program.durationWeeks} hafta
            </span>
            <span className="flex items-center gap-1">
              <Clock className="h-4 w-4" />
              {program.daysPerWeek} gün/hafta
            </span>
            <Badge variant="secondary">
              {difficultyLabels[program.difficulty - 1]}
            </Badge>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            onClick={() => {
              setCopyProgramName(`${program.name} (Kopya)`);
              setIsCopyProgramOpen(true);
            }}
          >
            <Copy className="mr-2 h-4 w-4" />
            Programı Kopyala
          </Button>
          <Button onClick={() => openEditor()}>
            <Plus className="mr-2 h-4 w-4" />
            Gün Ekle
          </Button>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-4">
        {/* Main Editor Section */}
        <div className="space-y-6 lg:col-span-3">
          {/* Week Selector */}
          <div className="flex flex-wrap items-center gap-4">
            <Button
              variant="outline"
              size="icon"
              onClick={() => setSelectedWeek((w) => Math.max(1, w - 1))}
              disabled={selectedWeek === 1}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>

            <Select
              value={selectedWeek.toString()}
              onValueChange={(v) => setSelectedWeek(Number.parseInt(v))}
            >
              <SelectTrigger className="w-[160px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Array.from({ length: totalWeeks }, (_, i) => (
                  <SelectItem key={i + 1} value={(i + 1).toString()}>
                    Hafta {i + 1}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Button
              variant="outline"
              size="icon"
              onClick={() =>
                setSelectedWeek((w) => Math.min(totalWeeks, w + 1))
              }
              disabled={selectedWeek === totalWeeks}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>

            <span className="text-muted-foreground text-sm">
              {weekDays.length} gün tanımlı
            </span>

            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setTargetWeek(selectedWeek < totalWeeks ? selectedWeek + 1 : 1);
                setIsCopyWeekOpen(true);
              }}
              disabled={weekDays.length === 0}
            >
              <Copy className="mr-2 h-4 w-4" />
              Haftayı Kopyala
            </Button>
          </div>

          {/* Days Grid */}
          {weekDays.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Calendar className="mb-4 h-12 w-12 text-muted-foreground" />
                <h3 className="mb-2 font-semibold text-lg">
                  Hafta {selectedWeek} için gün yok
                </h3>
                <p className="mb-4 text-center text-muted-foreground">
                  Antrenman günlerini ekleyerek programı oluşturun
                </p>
                <Button onClick={() => openEditor()}>
                  <Plus className="mr-2 h-4 w-4" />
                  İlk Günü Ekle
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2">
              {weekDays
                .sort(
                  (a: { dayNumber: number }, b: { dayNumber: number }) =>
                    a.dayNumber - b.dayNumber,
                )
                .map((day: SelectProgramDay) => (
                  <Card
                    key={day.dayId}
                    className="cursor-pointer transition-all hover:ring-2 hover:ring-primary/50"
                    onClick={() => openEditor(day)}
                  >
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-base">
                          Gün {day.dayNumber}: {day.dayName}
                        </CardTitle>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-1">
                        {(day.exercises ?? []).slice(0, 4).map((ex, idx) => (
                          <div
                            key={ex.exerciseId || idx}
                            className="text-muted-foreground text-sm"
                          >
                            • {ex.exerciseName || "İsimsiz"} ({ex.sets}×
                            {ex.reps})
                          </div>
                        ))}
                        {(day.exercises ?? []).length > 4 && (
                          <div className="text-muted-foreground text-xs">
                            +{(day.exercises ?? []).length - 4} hareket daha
                          </div>
                        )}
                        {(day.exercises ?? []).length === 0 && (
                          <div className="text-muted-foreground text-sm italic">
                            Hareket eklenmemiş
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
            </div>
          )}
        </div>

        {/* Sidebar Section - Program Access */}
        <div className="space-y-6">
          <ProgramAccessCard programId={programId} program={program} />
        </div>
      </div>

      {/* Day Editor Dialog */}
      <Dialog open={isEditorOpen} onOpenChange={setIsEditorOpen}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingDay
                ? `Gün Düzenle: ${editingDay.dayName}`
                : "Yeni Gün Ekle"}
            </DialogTitle>
          </DialogHeader>
          <DayEditor
            dayName={dayName}
            exercises={exercises}
            notes={notes}
            onNameChange={setDayName}
            onExercisesChange={setExercises}
            onNotesChange={setNotes}
            onSave={handleSave}
            onCancel={closeEditor}
            onDelete={editingDay ? () => setDeleteDay(editingDay) : undefined}
            isLoading={
              createDayMutation.isPending || updateDayMutation.isPending
            }
            availableExercises={availableExercises}
          />
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog
        open={!!deleteDay}
        onOpenChange={(open) => !open && setDeleteDay(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Günü Sil</AlertDialogTitle>
            <AlertDialogDescription>
              "{deleteDay?.dayName}" gününü silmek istediğinize emin misiniz?
              Tüm hareket verileri de silinecektir.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteDayMutation.isPending}>
              İptal
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteDay}
              disabled={deleteDayMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Sil
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Copy Week Dialog */}
      <Dialog open={isCopyWeekOpen} onOpenChange={setIsCopyWeekOpen}>
        <DialogContent className="max-w-md sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Haftayı Kopyala</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <p className="text-muted-foreground text-sm">
                Hafta {selectedWeek}'i ({weekDays.length} gün) hangi haftaya
                kopyalamak istersiniz?
              </p>
              <Select
                value={targetWeek.toString()}
                onValueChange={(v) => setTargetWeek(Number.parseInt(v))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: totalWeeks }, (_, i) => i + 1)
                    .filter((w) => w !== selectedWeek)
                    .map((w) => {
                      const weekDaysCount = days.filter(
                        (d: { weekNumber: number }) => d.weekNumber === w,
                      ).length;
                      return (
                        <SelectItem key={w} value={w.toString()}>
                          Hafta {w} ({weekDaysCount} gün)
                        </SelectItem>
                      );
                    })}
                </SelectContent>
              </Select>
            </div>
            {days.filter(
              (d: { weekNumber: number }) => d.weekNumber === targetWeek,
            ).length > 0 && (
              <p className="text-amber-600 text-sm">
                ⚠️ Hedef haftada zaten günler var. Kopyalama işlemi bu günlere ek
                olarak eklenecektir.
              </p>
            )}
          </div>
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setIsCopyWeekOpen(false)}
              disabled={duplicateWeekMutation.isPending}
            >
              İptal
            </Button>
            <Button
              onClick={() =>
                duplicateWeekMutation.mutate({
                  data: {
                    programId,
                    sourceWeek: selectedWeek,
                    targetWeek,
                  },
                })
              }
              disabled={duplicateWeekMutation.isPending}
            >
              {duplicateWeekMutation.isPending ? "Kopyalanıyor..." : "Kopyala"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Copy Entire Program Dialog */}
      <Dialog open={isCopyProgramOpen} onOpenChange={setIsCopyProgramOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Programı Kopyala</DialogTitle>
          </DialogHeader>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              duplicateProgramMutation.mutate({
                data: {
                  programId,
                  name: copyProgramName.trim() || undefined,
                },
              });
            }}
            className="space-y-4"
          >
            <p className="text-muted-foreground text-sm">
              <strong className="text-foreground">"{program.name}"</strong>{" "}
              programının tüm haftaları, günleri ve egzersiz içerikleri yeni bir
              program olarak kopyalanacaktır.
            </p>
            <div className="space-y-2">
              <Label htmlFor="detail-copy-program-name">Yeni Program Adı</Label>
              <Input
                id="detail-copy-program-name"
                value={copyProgramName}
                onChange={(e) => setCopyProgramName(e.target.value)}
                placeholder="Program Adı (Kopya)"
                required
                disabled={duplicateProgramMutation.isPending}
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsCopyProgramOpen(false)}
                disabled={duplicateProgramMutation.isPending}
              >
                İptal
              </Button>
              <Button
                type="submit"
                disabled={duplicateProgramMutation.isPending}
              >
                {duplicateProgramMutation.isPending
                  ? "Kopyalanıyor..."
                  : "Kopyala"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ProgramAccessCard({
  programId,
  program,
}: { programId: string; program: any }) {
  const { queryClient } = useRouteContext({
    from: "/_app/admin/program/$programId",
  });
  const { data: countData } = useQuery(
    getProgramParticipantCountOptions(programId),
  );

  const participantCount = countData?.body?.count ?? 0;

  const togglePublicMutation = useMutation({
    mutationFn: (data: { data: { programId: string } }) =>
      toggleProgramPublicFn(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getProgramOptions(programId).queryKey,
      });
      toast.success("Görünürlük güncellendi");
    },
  });

  const generateCodeMutation = useMutation({
    mutationFn: (data: { data: { programId: string } }) =>
      generateProgramAccessCodeFn(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getProgramOptions(programId).queryKey,
      });
      toast.success("Erişim kodu yenilendi");
    },
  });

  const clearCodeMutation = useMutation({
    mutationFn: (data: { data: { programId: string } }) =>
      clearProgramAccessCodeFn(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getProgramOptions(programId).queryKey,
      });
      toast.success("Erişim kodu kaldırıldı ve program ücretsiz yapıldı");
    },
  });

  const updateProgramMutation = useMutation({
    mutationFn: (data: {
      data: { programId: string; maxParticipants: number | null };
    }) => updateProgramFn(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getProgramOptions(programId).queryKey,
      });
      toast.success("Program güncellendi");
    },
  });

  const updateIntroVideoMutation = useMutation({
    mutationFn: (data: {
      data: { programId: string; introVideoUrl: string | null };
    }) => updateProgramFn(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getProgramOptions(programId).queryKey,
      });
      toast.success("Bilgilendirme videosu güncellendi");
    },
  });

  const copyLink = () => {
    const link = `${window.location.origin}/join/${program.accessCode}`;
    navigator.clipboard.writeText(link);
    toast.success("Katılım linki kopyalandı");
  };

  return (
    <Card className="overflow-hidden border-primary/10 shadow-md">
      <CardHeader className="bg-primary/5 pb-4">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Share2 className="h-5 w-5 text-primary" />
            Program Erişimi
          </CardTitle>
          <Button
            asChild
            variant="ghost"
            size="sm"
            className="h-8 gap-1.5 text-primary hover:bg-primary/10"
          >
            <Link to="/admin/users" search={{ q: program.name }}>
              <TrendingUp className="h-4 w-4" />
              Takip Et
            </Link>
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-6 pt-6">
        {/* Toggle Public */}
        <div className="flex items-center justify-between rounded-lg border border-border/50 bg-muted/30 p-3">
          <div className="space-y-0.5">
            <Label className="flex items-center gap-2 font-semibold text-sm">
              {program.isPublic ? (
                <Globe className="h-4 w-4" />
              ) : (
                <Lock className="h-4 w-4" />
              )}
              {program.isPublic ? "Herkese Açık" : "Gizli (Özel)"}
            </Label>
            <p className="text-muted-foreground text-xs">
              {program.isPublic
                ? "Kütüphanede herkes görebilir"
                : "Sadece link ile erişilebilir"}
            </p>
          </div>
          <Switch
            checked={program.isPublic}
            onCheckedChange={() =>
              togglePublicMutation.mutate({ data: { programId } })
            }
            disabled={togglePublicMutation.isPending}
          />
        </div>

        {/* Access Code Section */}
        <div className="space-y-3">
          <Label className="font-semibold text-sm">Erişim Kodu</Label>
          <div className="flex gap-2">
            <div className="flex flex-1 items-center justify-center rounded border border-border bg-muted p-2 font-bold font-mono text-lg tracking-widest">
              {program.accessCode || "KOD YOK"}
            </div>
            <Button
              size="icon"
              variant="outline"
              onClick={() =>
                generateCodeMutation.mutate({ data: { programId } })
              }
              disabled={
                generateCodeMutation.isPending || clearCodeMutation.isPending
              }
              title="Kodu Yenile"
            >
              <RefreshCw
                className={`h-4 w-4 ${generateCodeMutation.isPending ? "animate-spin" : ""}`}
              />
            </Button>
            {program.accessCode && (
              <Button
                size="icon"
                variant="destructive"
                onClick={() => {
                  if (
                    confirm(
                      "Bu programın erişim kodunu silmek ve programı ücretsiz yapmak istediğinizden emin misiniz?",
                    )
                  ) {
                    clearCodeMutation.mutate({ data: { programId } });
                  }
                }}
                disabled={
                  generateCodeMutation.isPending || clearCodeMutation.isPending
                }
                title="Kodu Sil"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
          {program.accessCode && (
            <Button variant="secondary" className="w-full" onClick={copyLink}>
              <ExternalLink className="mr-2 h-4 w-4" />
              Katılım Linkini Kopyala
            </Button>
          )}
        </div>

        {/* Participant Limits */}
        <div className="space-y-4 border-border/50 border-t pt-2">
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-primary" />
              <span className="font-semibold text-muted-foreground">
                Mevcut Katılımcı:
              </span>
            </div>
            <Badge variant="outline" className="font-bold text-base">
              {participantCount}{" "}
              {program.maxParticipants ? `/ ${program.maxParticipants}` : ""}
            </Badge>
          </div>

          <div className="space-y-2">
            <Label className="font-semibold text-muted-foreground text-xs">
              Maksimum Katılımcı Sınırı
            </Label>
            <div className="flex gap-2">
              <Input
                type="number"
                placeholder="Sınırsız"
                className="h-9"
                defaultValue={program.maxParticipants || ""}
                onBlur={(e) => {
                  const val =
                    e.target.value === ""
                      ? null
                      : Number.parseInt(e.target.value);
                  if (val !== program.maxParticipants) {
                    updateProgramMutation.mutate({
                      data: { programId, maxParticipants: val },
                    });
                  }
                }}
              />
              <Button size="sm" variant="ghost" disabled>
                Kişi
              </Button>
            </div>
            <p className="text-[10px] text-muted-foreground italic">
              Boş bırakılırsa sınırsız kişi katılabilir.
            </p>
          </div>

          {/* Intro Video */}
          <div className="space-y-3 border-border/50 border-t pt-2">
            <Label className="flex items-center gap-2 font-semibold text-sm">
              <Video className="h-4 w-4 text-primary" />
              Bilgilendirme Videosu
            </Label>
            <div className="flex gap-2">
              <Input
                placeholder="Video URL (YouTube, Vimeo vs.)"
                className="h-9"
                defaultValue={program.introVideoUrl || ""}
                onBlur={(e) => {
                  const val = e.target.value === "" ? null : e.target.value;
                  if (val !== program.introVideoUrl) {
                    updateIntroVideoMutation.mutate({
                      data: { programId, introVideoUrl: val },
                    });
                  }
                }}
              />
            </div>
            <p className="text-[10px] text-muted-foreground italic">
              Kullanıcılara program detayında gösterilecek video linki.
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function EditorSkeleton() {
  return (
    <div className="space-y-6">
      <Skeleton className="h-10 w-2/3" />
      <Skeleton className="h-6 w-1/3" />
      <div className="flex gap-4">
        <Skeleton className="h-10 w-10" />
        <Skeleton className="h-10 w-[160px]" />
        <Skeleton className="h-10 w-10" />
      </div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <Skeleton className="h-32" />
        <Skeleton className="h-32" />
        <Skeleton className="h-32" />
      </div>
    </div>
  );
}
